import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Button, Col, Empty, Form, Input, Modal, Row, Select, Space, Tag, Tooltip, Typography,
} from 'antd';
import {
  ApartmentOutlined, DeleteOutlined, NodeExpandOutlined, PlusOutlined, SaveOutlined,
} from '@ant-design/icons';
import { message } from 'antd';
import ReactFlow, { Background, Controls, Edge, Node, BackgroundVariant, useNodesState, useEdgesState, useReactFlow, ReactFlowProvider, Handle, Position, NodeProps, MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';
import { Ontology, OntologyEntity, OntologyPropertyType, OntologyRelationship } from '../types';
import { saveOntology, fetchOntology } from '../services/api.service';

const { Text } = Typography;

// ── Palette ──────────────────────────────────────────────────────────────────
const ENTITY_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#06b6d4'];

const PROPERTY_TYPE_COLORS: Record<OntologyPropertyType, string> = {
  string: 'blue',
  number: 'green',
  boolean: 'orange',
  date: 'purple',
};

// ── ID helpers ────────────────────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 9);

// ── Mock seed data ────────────────────────────────────────────────────────────
const SEED: Ontology[] = [
  {
    id: 'ont-1',
    agentId: '',
    name: 'Main',
    entities: [
      {
        id: 'ent-1',
        name: 'Company',
        color: ENTITY_COLORS[0],
        properties: [
          { id: 'p-1', name: 'name', type: 'string' },
          { id: 'p-2', name: 'sector', type: 'string' },
          { id: 'p-3', name: 'employees', type: 'number' },
        ],
      },
      {
        id: 'ent-2',
        name: 'Person',
        color: ENTITY_COLORS[1],
        properties: [
          { id: 'p-4', name: 'name', type: 'string' },
          { id: 'p-5', name: 'email', type: 'string' },
          { id: 'p-6', name: 'active', type: 'boolean' },
        ],
      },
    ],
    relationships: [
      { id: 'rel-1', name: 'WORKS_AT', sourceEntityId: 'ent-2', targetEntityId: 'ent-1' },
    ],
  },
];

// ── Props ─────────────────────────────────────────────────────────────────────
interface OntologyPanelProps {
  agentId: string | null;
}

// ── Component ─────────────────────────────────────────────────────────────────
const OntologyPanel: React.FC<OntologyPanelProps> = ({ agentId }) => {
  const [ontologies, setOntologies] = useState<Ontology[]>(
    SEED.map((o) => ({ ...o, agentId: agentId ?? '' })),
  );
  const [selectedId, setSelectedId] = useState<string>(SEED[0].id);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // ── Load from backend ──
  useEffect(() => {
    if (!agentId || loaded) return;
    fetchOntology(agentId).then((res) => {
      if (res?.ontology_data?.ontologies) {
        const saved = (res.ontology_data.ontologies as unknown) as Ontology[];
        if (saved.length > 0) {
          setOntologies(saved);
          setSelectedId(saved[0].id);
        }
      }
      setLoaded(true);
    }).catch(() => {
      setLoaded(true);
    });
  }, [agentId, loaded]);

  // ── Save to backend ──
  const handleSave = useCallback(async () => {
    if (!agentId) {
      message.warning('Save the agent first');
      return;
    }
    setSaving(true);
    try {
      await saveOntology(agentId, ontologies);
      message.success('Ontology saved');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      message.error(`Error saving ontology: ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [agentId, ontologies]);

  // ── Modal state ──
  const [ontologyModalOpen, setOntologyModalOpen] = useState(false);
  const [entityModalOpen, setEntityModalOpen] = useState(false);
  const [propertyModal, setPropertyModal] = useState<{ entityId: string } | null>(null);
  const [relationshipModalOpen, setRelationshipModalOpen] = useState(false);

  const [ontologyForm] = Form.useForm();
  const [entityForm] = Form.useForm();
  const [propertyForm] = Form.useForm();
  const [relationshipForm] = Form.useForm();

  // ── Derived ──
  const current = useMemo(
    () => ontologies.find((o) => o.id === selectedId),
    [ontologies, selectedId],
  );

  const updateCurrent = useCallback(
    (updater: (o: Ontology) => Ontology) => {
      setOntologies((prev) => prev.map((o) => (o.id === selectedId ? updater(o) : o)));
    },
    [selectedId],
  );

  // ── React Flow graph ──
  const { rfNodes, rfEdges } = useMemo(() => {
    if (!current) return { rfNodes: [], rfEdges: [] };

    const count = current.entities.length;
    const radius = count <= 2 ? 120 : 160;
    const cx = 300;
    const cy = 160;

    const rfNodes: Node[] = current.entities.map((entity, i) => {
      const angle = (2 * Math.PI * i) / count - Math.PI / 2;
      return {
        id: entity.id,
        type: 'circle',
        data: { label: entity.name, color: entity.color },
        position: {
          x: cx + radius * Math.cos(angle) - 45,
          y: cy + radius * Math.sin(angle) - 45,
        },
      };
    });

    // Detect parallel edges (same pair in either direction) and assign different handle pairs
    const HANDLE_PAIRS: Array<{ sourceHandle: string; targetHandle: string }> = [
      { sourceHandle: 'right', targetHandle: 'left-t' },
      { sourceHandle: 'bottom', targetHandle: 'top-t' },
      { sourceHandle: 'top', targetHandle: 'bottom-t' },
      { sourceHandle: 'left', targetHandle: 'right-t' },
    ];
    const pairCount = new Map<string, number>();

    const rfEdges: Edge[] = current.relationships.map((rel) => {
      const pairKey = [rel.sourceEntityId, rel.targetEntityId].sort().join('::');
      const idx = pairCount.get(pairKey) ?? 0;
      pairCount.set(pairKey, idx + 1);
      const handles = HANDLE_PAIRS[idx % HANDLE_PAIRS.length];

      return {
        id: rel.id,
        source: rel.sourceEntityId,
        target: rel.targetEntityId,
        sourceHandle: handles.sourceHandle,
        targetHandle: handles.targetHandle,
        label: rel.name,
        type: 'smoothstep',
        style: { stroke: '#94a3b8', strokeWidth: 2 },
        labelStyle: { fill: '#475569', fontWeight: 700, fontSize: 11 },
        labelBgStyle: { fill: '#f1f5f9', fillOpacity: 0.9 },
        labelBgPadding: [4, 6] as [number, number],
        markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
      };
    });

    return { rfNodes, rfEdges };
  }, [current]);


  // ── Handlers: Ontology ──
  const handleAddOntology = () => {
    ontologyForm.validateFields().then((values: { name: string }) => {
      const newOnt: Ontology = {
        id: uid(),
        agentId: agentId ?? '',
        name: values.name,
        entities: [],
        relationships: [],
      };
      setOntologies((prev) => [...prev, newOnt]);
      setSelectedId(newOnt.id);
      setOntologyModalOpen(false);
      ontologyForm.resetFields();
    });
  };

  const handleDeleteOntology = (id: string) => {
    const remaining = ontologies.filter((o) => o.id !== id);
    setOntologies(remaining);
    if (selectedId === id) {
      setSelectedId(remaining[0]?.id ?? '');
    }
  };

  // ── Handlers: Entity ──
  const handleAddEntity = () => {
    entityForm.validateFields().then((values: { name: string }) => {
      updateCurrent((o) => ({
        ...o,
        entities: [
          ...o.entities,
          {
            id: uid(),
            name: values.name,
            color: ENTITY_COLORS[o.entities.length % ENTITY_COLORS.length],
            properties: [],
          },
        ],
      }));
      setEntityModalOpen(false);
      entityForm.resetFields();
    });
  };

  const handleDeleteEntity = (entityId: string) => {
    updateCurrent((o) => ({
      ...o,
      entities: o.entities.filter((e) => e.id !== entityId),
      relationships: o.relationships.filter(
        (r) => r.sourceEntityId !== entityId && r.targetEntityId !== entityId,
      ),
    }));
  };

  // ── Handlers: Property ──
  const handleAddProperty = () => {
    if (!propertyModal) return;
    propertyForm.validateFields().then((values: { name: string; type: OntologyPropertyType }) => {
      updateCurrent((o) => ({
        ...o,
        entities: o.entities.map((e) =>
          e.id === propertyModal.entityId
            ? { ...e, properties: [...e.properties, { id: uid(), name: values.name, type: values.type }] }
            : e,
        ),
      }));
      setPropertyModal(null);
      propertyForm.resetFields();
    });
  };

  const handleDeleteProperty = (entityId: string, propId: string) => {
    updateCurrent((o) => ({
      ...o,
      entities: o.entities.map((e) =>
        e.id === entityId ? { ...e, properties: e.properties.filter((p) => p.id !== propId) } : e,
      ),
    }));
  };

  // ── Handlers: Relationship ──
  const handleAddRelationship = () => {
    relationshipForm.validateFields().then(
      (values: { name: string; sourceEntityId: string; targetEntityId: string }) => {
        updateCurrent((o) => ({
          ...o,
          relationships: [
            ...o.relationships,
            { id: uid(), ...values, name: values.name.toUpperCase() },
          ],
        }));
        setRelationshipModalOpen(false);
        relationshipForm.resetFields();
      },
    );
  };

  const handleDeleteRelationship = (relId: string) => {
    updateCurrent((o) => ({
      ...o,
      relationships: o.relationships.filter((r) => r.id !== relId),
    }));
  };

  // ── Empty state ──
  if (ontologies.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <Empty
          image={<ApartmentOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
          description={<Text type="secondary">No ontologies defined for this agent</Text>}
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOntologyModalOpen(true)}>
            New Ontology
          </Button>
        </Empty>
        <OntologyModal open={ontologyModalOpen} form={ontologyForm} onOk={handleAddOntology} onCancel={() => setOntologyModalOpen(false)} />
      </div>
    );
  }

  return (
    <div style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* ── Header: ontology tabs ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
        <NodeExpandOutlined style={{ color: '#6366f1', fontSize: 16 }} />
        <Text strong style={{ marginRight: 8 }}>Ontologies</Text>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
          {ontologies.map((o) => (
            <Tag
              key={o.id}
              color={o.id === selectedId ? 'geekblue' : 'default'}
              style={{ cursor: 'pointer', userSelect: 'none', padding: '2px 10px' }}
              onClick={() => setSelectedId(o.id)}
              closable={ontologies.length > 1}
              onClose={(e) => { e.preventDefault(); handleDeleteOntology(o.id); }}
            >
              {o.name}
            </Tag>
          ))}
        </div>
        <Button size="small" icon={<PlusOutlined />} onClick={() => setOntologyModalOpen(true)}>
          New
        </Button>
        <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave} disabled={!agentId}>
          Save
        </Button>
      </div>

      {/* ── Split panel ── */}
      {current && (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Row style={{ flex: '0 0 55%', overflow: 'hidden', borderBottom: '1px solid #f0f0f0' }}>

            {/* ── Left: Entities ── */}
            <Col span={10} style={{ height: '100%', overflow: 'auto', borderRight: '1px solid #f0f0f0', padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Text strong style={{ fontSize: 12, textTransform: 'uppercase', color: '#8c8c8c', letterSpacing: 1 }}>
                  Entities
                </Text>
                <Button size="small" icon={<PlusOutlined />} onClick={() => setEntityModalOpen(true)}>
                  Add
                </Button>
              </div>

              {current.entities.length === 0 && (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No entities" />
              )}

              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                {current.entities.map((entity) => (
                  <EntityCard
                    key={entity.id}
                    entity={entity}
                    onDelete={() => handleDeleteEntity(entity.id)}
                    onDeleteProperty={(propId) => handleDeleteProperty(entity.id, propId)}
                    onAddProperty={() => setPropertyModal({ entityId: entity.id })}
                  />
                ))}
              </Space>
            </Col>

            {/* ── Right: Relationships ── */}
            <Col span={14} style={{ height: '100%', overflow: 'auto', padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Text strong style={{ fontSize: 12, textTransform: 'uppercase', color: '#8c8c8c', letterSpacing: 1 }}>
                  Relationships
                </Text>
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  disabled={current.entities.length < 2}
                  onClick={() => setRelationshipModalOpen(true)}
                >
                  Add
                </Button>
              </div>

              {current.relationships.length === 0 && (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No relationships" />
              )}

              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                {current.relationships.map((rel) => {
                  const src = current.entities.find((e) => e.id === rel.sourceEntityId);
                  const tgt = current.entities.find((e) => e.id === rel.targetEntityId);
                  return (
                    <RelationshipRow
                      key={rel.id}
                      rel={rel}
                      src={src}
                      tgt={tgt}
                      onDelete={() => handleDeleteRelationship(rel.id)}
                    />
                  );
                })}
              </Space>
            </Col>
          </Row>

          {/* ── Bottom: Graph preview ── */}
          <div style={{ flex: 1, minHeight: 300, position: 'relative', background: '#fafbfc' }}>
            <div style={{ position: 'absolute', top: 8, left: 12, zIndex: 10 }}>
              <Text style={{ fontSize: 11, textTransform: 'uppercase', color: '#bfbfbf', letterSpacing: 1 }}>
                Graph preview
              </Text>
            </div>
            <ReactFlowProvider>
              <OntologyGraph nodes={rfNodes} edges={rfEdges} />
            </ReactFlowProvider>
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      <OntologyModal open={ontologyModalOpen} form={ontologyForm} onOk={handleAddOntology} onCancel={() => { setOntologyModalOpen(false); ontologyForm.resetFields(); }} />

      <Modal title="New entity" open={entityModalOpen} onOk={handleAddEntity} onCancel={() => { setEntityModalOpen(false); entityForm.resetFields(); }} okText="Create">
        <Form form={entityForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
            <Input placeholder="Company, Product, Event..." autoFocus />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="New property" open={!!propertyModal} onOk={handleAddProperty} onCancel={() => { setPropertyModal(null); propertyForm.resetFields(); }} okText="Add">
        <Form form={propertyForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="name, start_date, active..." autoFocus />
          </Form.Item>
          <Form.Item name="type" label="Type" rules={[{ required: true }]} initialValue="string">
            <Select>
              <Select.Option value="string">string</Select.Option>
              <Select.Option value="number">number</Select.Option>
              <Select.Option value="boolean">boolean</Select.Option>
              <Select.Option value="date">date</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {current && (
        <Modal title="New relationship" open={relationshipModalOpen} onOk={handleAddRelationship} onCancel={() => { setRelationshipModalOpen(false); relationshipForm.resetFields(); }} okText="Create">
          <Form form={relationshipForm} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item name="sourceEntityId" label="Source entity" rules={[{ required: true }]}>
              <Select placeholder="Select...">
                {current.entities.map((e) => (
                  <Select.Option key={e.id} value={e.id}>{e.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="name" label="Relationship name" rules={[{ required: true }]}>
              <Input placeholder="WORKS_AT, COMPETES_WITH..." />
            </Form.Item>
            <Form.Item name="targetEntityId" label="Target entity" rules={[{ required: true }]}>
              <Select placeholder="Select...">
                {current.entities.map((e) => (
                  <Select.Option key={e.id} value={e.id}>{e.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

interface EntityCardProps {
  entity: OntologyEntity;
  onDelete: () => void;
  onDeleteProperty: (propId: string) => void;
  onAddProperty: () => void;
}

const EntityCard: React.FC<EntityCardProps> = ({ entity, onDelete, onDeleteProperty, onAddProperty }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', background: entity.color + '18', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: entity.color, marginRight: 8, flexShrink: 0 }} />
        <Text strong style={{ flex: 1, fontSize: 13 }}>{entity.name}</Text>
        <Text type="secondary" style={{ fontSize: 11, marginRight: 8 }}>{entity.properties.length} props</Text>
        <Tooltip title="Delete entity">
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            danger
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          />
        </Tooltip>
      </div>

      {expanded && (
        <div style={{ padding: '8px 12px', background: '#fff' }}>
          {entity.properties.map((prop) => (
            <div key={prop.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
              <Text style={{ flex: 1, fontSize: 12, color: '#595959' }}>· {prop.name}</Text>
              <Tag color={PROPERTY_TYPE_COLORS[prop.type]} style={{ fontSize: 10, margin: '0 8px 0 0' }}>{prop.type}</Tag>
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined style={{ fontSize: 10 }} />}
                onClick={() => onDeleteProperty(prop.id)}
                style={{ color: '#d9d9d9', padding: 0 }}
              />
            </div>
          ))}
          <Button
            type="dashed"
            size="small"
            icon={<PlusOutlined />}
            onClick={onAddProperty}
            style={{ marginTop: 4, width: '100%', fontSize: 11 }}
          >
            Property
          </Button>
        </div>
      )}
    </div>
  );
};

interface RelationshipRowProps {
  rel: OntologyRelationship;
  src: OntologyEntity | undefined;
  tgt: OntologyEntity | undefined;
  onDelete: () => void;
}

const RelationshipRow: React.FC<RelationshipRowProps> = ({ rel, src, tgt, onDelete }) => (
  <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 8, background: '#fff', gap: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, flexWrap: 'wrap' }}>
      <Tag color="default" style={{ borderRadius: 4, fontWeight: 600, background: src?.color + '20', borderColor: src?.color, color: src?.color }}>
        {src?.name ?? '?'}
      </Tag>
      <Text style={{ fontSize: 11, color: '#8c8c8c' }}>──</Text>
      <Tag color="geekblue" style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 11 }}>
        {rel.name}
      </Tag>
      <Text style={{ fontSize: 11, color: '#8c8c8c' }}>──▶</Text>
      <Tag color="default" style={{ borderRadius: 4, fontWeight: 600, background: tgt?.color + '20', borderColor: tgt?.color, color: tgt?.color }}>
        {tgt?.name ?? '?'}
      </Tag>
    </div>
    <Tooltip title="Delete relationship">
      <Button type="text" size="small" icon={<DeleteOutlined />} danger onClick={onDelete} />
    </Tooltip>
  </div>
);

interface OntologyModalProps {
  open: boolean;
  form: ReturnType<typeof Form.useForm>[0];
  onOk: () => void;
  onCancel: () => void;
}

const OntologyModal: React.FC<OntologyModalProps> = ({ open, form, onOk, onCancel }) => (
  <Modal title="New ontology" open={open} onOk={onOk} onCancel={onCancel} okText="Create">
    <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
      <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
        <Input placeholder="Main, Commercial, Product..." autoFocus />
      </Form.Item>
    </Form>
  </Modal>
);

// ── Circle custom node ────────────────────────────────────────────────────────
const CircleNode: React.FC<NodeProps<{ label: string; color: string }>> = ({ data }) => (
  <div
    style={{
      width: 90,
      height: 90,
      borderRadius: '50%',
      background: data.color,
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: 12,
      textAlign: 'center',
      padding: 8,
      boxSizing: 'border-box',
      boxShadow: '0 3px 12px rgba(0,0,0,0.18)',
      border: '3px solid rgba(255,255,255,0.45)',
      cursor: 'grab',
      userSelect: 'none',
      lineHeight: 1.2,
    }}
  >
    <Handle id="top" type="source" position={Position.Top} style={{ background: 'transparent', border: 'none' }} />
    <Handle id="right" type="source" position={Position.Right} style={{ background: 'transparent', border: 'none' }} />
    <Handle id="bottom" type="source" position={Position.Bottom} style={{ background: 'transparent', border: 'none' }} />
    <Handle id="left" type="source" position={Position.Left} style={{ background: 'transparent', border: 'none' }} />
    <Handle id="top-t" type="target" position={Position.Top} style={{ background: 'transparent', border: 'none' }} />
    <Handle id="right-t" type="target" position={Position.Right} style={{ background: 'transparent', border: 'none' }} />
    <Handle id="bottom-t" type="target" position={Position.Bottom} style={{ background: 'transparent', border: 'none' }} />
    <Handle id="left-t" type="target" position={Position.Left} style={{ background: 'transparent', border: 'none' }} />
    {data.label}
  </div>
);

const NODE_TYPES = { circle: CircleNode };

// ── Graph subcomponent — lives inside ReactFlowProvider so useReactFlow works ──
interface OntologyGraphProps {
  nodes: Node[];
  edges: Edge[];
}

const OntologyGraph: React.FC<OntologyGraphProps> = ({ nodes, edges }) => {
  const [flowNodes, setFlowNodes, onNodesChange] = useNodesState(nodes);
  const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState(edges);
  const { fitView } = useReactFlow();

  useEffect(() => {
    setFlowNodes((prev) => {
      const posMap = new Map(prev.map((n) => [n.id, n.position]));
      return nodes.map((n) => ({
        ...n,
        position: posMap.has(n.id) ? posMap.get(n.id)! : n.position,
      }));
    });
    setFlowEdges(edges);
    setTimeout(() => fitView({ padding: 0.35, duration: 300 }), 50);
  }, [nodes, edges, setFlowNodes, setFlowEdges, fitView]);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={NODE_TYPES}
        nodesDraggable
        elementsSelectable
        fitView
        fitViewOptions={{ padding: 0.35 }}
        nodesConnectable={false}
        zoomOnScroll
        panOnDrag
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e2e8f0" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

export default OntologyPanel;
